import { useState, useRef, useCallback } from "react";
import { type UserStory } from "@/lib/projectStore";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { GripVertical, Calculator, Map } from "lucide-react";

type Bucket = "now" | "next" | "later";

const BUCKETS: {
  id: Bucket;
  label: string;
  description: string;
  colClass: string;
  headerClass: string;
  badgeClass: string;
  emptyText: string;
}[] = [
  {
    id: "now",
    label: "Now",
    description: "Current sprint / quarter",
    colClass: "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800",
    headerClass: "bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800",
    badgeClass: "bg-green-100 text-green-800 border-green-300",
    emptyText: "Drop stories being actively worked this period",
  },
  {
    id: "next",
    label: "Next",
    description: "Next sprint / quarter",
    colClass: "bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800",
    headerClass: "bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800",
    badgeClass: "bg-blue-100 text-blue-800 border-blue-300",
    emptyText: "Drop stories committed for the next period",
  },
  {
    id: "later",
    label: "Later",
    description: "Future roadmap",
    colClass: "bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800",
    headerClass: "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800",
    badgeClass: "bg-amber-100 text-amber-800 border-amber-300",
    emptyText: "Drop stories planned for future quarters",
  },
];

const priorityDot: Record<UserStory["priority"], string> = {
  must: "bg-destructive",
  should: "bg-accent",
  could: "bg-blue-400",
  wont: "bg-muted-foreground/40",
};

function computeRice(s: UserStory["riceScore"]): number | null {
  if (!s || !s.effort) return null;
  return Math.round((s.reach * s.impact * (s.confidence / 100)) / s.effort);
}

interface Props {
  stories: UserStory[];
  onChange: (stories: UserStory[]) => void;
}

export default function RoadmapPanel({ stories, onChange }: Props) {
  const [dragId, setDragId] = useState<string | null>(null);
  const dragRef = useRef<string | null>(null);

  const assign = useCallback(
    (storyId: string, bucket: Bucket | undefined) => {
      onChange(stories.map(s => s.id === storyId ? { ...s, roadmapBucket: bucket } : s));
    },
    [stories, onChange]
  );

  const handleDragStart = (e: React.DragEvent, id: string) => {
    setDragId(id);
    dragRef.current = id;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", id);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent, bucket: Bucket) => {
      e.preventDefault();
      const id = e.dataTransfer.getData("text/plain") || dragRef.current;
      if (id) assign(id, bucket);
      setDragId(null);
      dragRef.current = null;
    },
    [assign]
  );

  const handleDragEnd = () => { setDragId(null); dragRef.current = null; };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };

  const bucketed: Record<Bucket, UserStory[]> = { now: [], next: [], later: [] };
  const unassigned: UserStory[] = [];

  stories.forEach(s => {
    if (s.roadmapBucket === "now" || s.roadmapBucket === "next" || s.roadmapBucket === "later") {
      bucketed[s.roadmapBucket].push(s);
    } else {
      unassigned.push(s);
    }
  });

  const doneCount = stories.filter(s => s.status === "done").length;
  const inProgressCount = stories.filter(s => s.status === "in-progress").length;

  return (
    <div className="h-full overflow-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Map className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
            Product Roadmap
          </h2>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground font-mono">
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-green-500 inline-block" />
            {doneCount} done
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500 inline-block" />
            {inProgressCount} in progress
          </span>
          <span className="flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-muted-foreground/30 inline-block" />
            {unassigned.length} unplaced
          </span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Drag stories into buckets to communicate sequencing. Reflects deliberate prioritisation decisions, not just a task list.
      </p>

      {/* Three-column roadmap */}
      <div className="overflow-x-auto -mx-1 px-1">
        <div className="flex gap-3 min-w-[640px] min-h-[360px]">
          {BUCKETS.map(bucket => {
            const items = bucketed[bucket.id];
            return (
              <div
                key={bucket.id}
                className={cn(
                  "flex-1 min-w-[200px] rounded-lg flex flex-col border",
                  bucket.colClass
                )}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, bucket.id)}
              >
                {/* Column header */}
                <div className={cn("px-4 py-3 border-b rounded-t-lg", bucket.headerClass)}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-display font-bold tracking-wide">{bucket.label}</p>
                      <p className="text-[10px] opacity-70 mt-0.5">{bucket.description}</p>
                    </div>
                    <span className="text-[10px] font-mono bg-white/40 px-1.5 py-0.5 rounded">
                      {items.length}
                    </span>
                  </div>
                </div>

                {/* Cards */}
                <div className="flex-1 p-2 space-y-1.5">
                  {items.map(story => {
                    const rice = computeRice(story.riceScore);
                    return (
                      <div
                        key={story.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, story.id)}
                        onDragEnd={handleDragEnd}
                        className={cn(
                          "rounded-md p-2.5 bg-card border border-border/60 cursor-grab active:cursor-grabbing transition-all hover:shadow-sm",
                          dragId === story.id && "opacity-30 scale-95",
                          story.status === "done" && "opacity-60"
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical className="h-3.5 w-3.5 text-muted-foreground/20 mt-0.5 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap mb-1">
                              <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", priorityDot[story.priority])} />
                              <p className="text-xs font-semibold text-card-foreground leading-snug">
                                {story.title}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {story.storyPoints && (
                                <span className="text-[9px] font-mono text-muted-foreground bg-secondary px-1 py-0.5 rounded">
                                  {story.storyPoints}p
                                </span>
                              )}
                              {rice !== null && (
                                <span className="text-[9px] font-mono text-violet-600 dark:text-violet-400 flex items-center gap-0.5">
                                  <Calculator className="h-2 w-2" />{rice}
                                </span>
                              )}
                              <Badge variant="outline" className={cn("text-[8px] px-1 py-0", bucket.badgeClass)}>
                                {story.status === "done" ? "✓ done" : story.status === "in-progress" ? "▶ active" : "backlog"}
                              </Badge>
                            </div>
                            {story.priority === "wont" && story.rejectionRationale && (
                              <p className="text-[10px] text-muted-foreground italic mt-1 line-clamp-2">
                                ⊘ {story.rejectionRationale}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {items.length === 0 && (
                    <div className="flex-1 min-h-[80px] rounded-md border border-dashed border-border/30 flex items-center justify-center px-3">
                      <span className="text-[10px] text-muted-foreground/40 text-center font-body">
                        {bucket.emptyText}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Unassigned stories */}
      {unassigned.length > 0 && (
        <div>
          <h3 className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider mb-2">
            Unplaced Stories ({unassigned.length}) — drag into a bucket above
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {unassigned.map(story => {
              const rice = computeRice(story.riceScore);
              return (
                <div
                  key={story.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, story.id)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "rounded-md p-2 bg-card border border-border cursor-grab active:cursor-grabbing transition-all hover:shadow-sm hover:border-muted-foreground/30",
                    dragId === story.id && "opacity-30 scale-95"
                  )}
                >
                  <div className="flex items-start gap-1.5">
                    <span className={cn("h-1.5 w-1.5 rounded-full mt-1.5 shrink-0", priorityDot[story.priority])} />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold truncate">{story.title}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {story.storyPoints && (
                          <span className="text-[9px] font-mono text-muted-foreground">{story.storyPoints}p</span>
                        )}
                        {rice !== null && (
                          <span className="text-[9px] font-mono text-violet-600 dark:text-violet-400 flex items-center gap-0.5">
                            <Calculator className="h-2 w-2" />{rice}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
